import {
  parseLoomWingBodyDefinitionV2,
  type LoomWingBodyDefinitionV2,
} from "../../api/loomRebuildV3";
import {
  parseLoomAuthoringDocument,
  type LoomSceneNode,
} from "./loomAuthoringModel";
import type { LoomWingBodyDefinition } from "./loomAuthoritativeDefinition";
import type {
  LoomPresetAdaptation,
  LoomPresetNormalizationReceipt,
  LoomPresetOmittedComponent,
  LoomPresetSelectedComponent,
} from "./loomPresetReceipt";
import {
  createLoomPresetV3Replacement,
  type LoomPresetQcJson,
} from "./loomPresetV3Binding";

interface LoomAirshowNativePresetBase {
  adaptations: readonly LoomPresetAdaptation[];
  definitionRevision: string;
  normalization: LoomPresetNormalizationReceipt;
  omittedComponents: readonly LoomPresetOmittedComponent[];
  presetRevision: string;
  qcReceipt: Readonly<Record<string, LoomPresetQcJson>>;
  replacement: { nodes: readonly LoomSceneNode[]; rootNodeIds: readonly string[] };
  selectedComponents: readonly LoomPresetSelectedComponent[];
  sourceScope: "selected-components-only";
}

export interface LoomAirshowNativePresetV2 extends LoomAirshowNativePresetBase {
  acceptedAssetSha256: `sha256:${string}`;
  contractVersion: "v2";
  definition: LoomWingBodyDefinition;
  evaluatorId: "loom.native-wing-body/v2";
  topology: "canonical-starboard-wing-fuselage-union";
}

export interface LoomAirshowNativePresetV3 extends LoomAirshowNativePresetBase {
  acceptedAssetSha256: `sha256:${string}`;
  acceptedMeshSchema: "loom.accepted-mesh/v3";
  contractVersion: "v3";
  definition: LoomWingBodyDefinitionV2;
  evaluatorId: "loom.native-wing-body/v3";
  topology: "selected-wing-fuselage-pairwise-union";
  unionAlgorithmId: "loom.implicit-guided-explicit-union/v1";
}

export type LoomAirshowNativePreset =
  | LoomAirshowNativePresetV2
  | LoomAirshowNativePresetV3;

type UnknownRecord = Record<string, unknown>;

function record(value: unknown, path: string): UnknownRecord {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new Error(`${path} must be an object`);
  }
  return value as UnknownRecord;
}

function exactKeys(value: UnknownRecord, allowed: readonly string[], path: string): void {
  const unknown = Object.keys(value).filter((key) => !allowed.includes(key));
  const missing = allowed.filter((key) => !(key in value));
  if (unknown.length || missing.length) throw new Error(`${path} has an unsupported field set`);
}

function text(value: unknown, path: string): string {
  if (typeof value !== "string" || value.trim() === "") {
    throw new Error(`${path} must be a non-empty string`);
  }
  return value;
}

function positive(value: unknown, path: string): number {
  if (typeof value !== "number" || !Number.isFinite(value) || value <= 0) {
    throw new Error(`${path} must be positive`);
  }
  return value;
}

function nonnegativeInteger(value: unknown, path: string): number {
  if (typeof value !== "number" || !Number.isSafeInteger(value) || value < 0) {
    throw new Error(`${path} must be a nonnegative integer`);
  }
  return value;
}

function sha256(value: unknown, path: string): string {
  const result = text(value, path);
  if (!/^(?:sha256:)?[a-f0-9]{64}$/.test(result)) {
    throw new Error(`${path} must be a SHA-256 identity`);
  }
  return result;
}

function parseSelected(value: unknown, path: string): LoomPresetSelectedComponent[] {
  if (!Array.isArray(value) || value.length !== 2) {
    throw new Error(`${path} must contain one wing and one fuselage`);
  }
  const parsed = value.map((item, index): LoomPresetSelectedComponent => {
    const itemPath = `${path}[${index}]`;
    const row = record(item, itemPath);
    exactKeys(row, ["componentId", "role"], itemPath);
    if (row.role !== "wing" && row.role !== "fuselage") {
      throw new Error(`${itemPath}.role is unsupported`);
    }
    return { componentId: text(row.componentId, `${itemPath}.componentId`), role: row.role };
  });
  if (new Set(parsed.map(({ componentId }) => componentId)).size !== 2
      || new Set(parsed.map(({ role }) => role)).size !== 2) {
    throw new Error(`${path} must identify distinct wing and fuselage sources`);
  }
  return parsed;
}

function parseOmissions(value: unknown, path: string): LoomPresetOmittedComponent[] {
  if (!Array.isArray(value)) throw new Error(`${path} must be an array`);
  const parsed = value.map((item, index): LoomPresetOmittedComponent => {
    const itemPath = `${path}[${index}]`;
    const row = record(item, itemPath);
    exactKeys(row, ["componentId", "name", "type", "reason"], itemPath);
    return {
      componentId: text(row.componentId, `${itemPath}.componentId`),
      name: text(row.name, `${itemPath}.name`),
      reason: text(row.reason, `${itemPath}.reason`),
      type: text(row.type, `${itemPath}.type`),
    };
  });
  if (new Set(parsed.map(({ componentId }) => componentId)).size !== parsed.length) {
    throw new Error(`${path} contains duplicates`);
  }
  return parsed;
}

function parseAdaptations(value: unknown, path: string): LoomPresetAdaptation[] {
  if (!Array.isArray(value)) throw new Error(`${path} must be an array`);
  return value.map((item, index): LoomPresetAdaptation => {
    const itemPath = `${path}[${index}]`;
    const row = record(item, itemPath);
    exactKeys(row, ["kind", "componentId", "reason", "sourceValue", "derivedValue"], itemPath);
    const scalar = (candidate: unknown, field: string) => {
      if (candidate === null || typeof candidate === "boolean" || typeof candidate === "string") {
        return candidate;
      }
      if (typeof candidate === "number" && Number.isFinite(candidate)) return candidate;
      throw new Error(`${itemPath}.${field} must be a finite scalar`);
    };
    return {
      componentId: text(row.componentId, `${itemPath}.componentId`),
      derivedValue: scalar(row.derivedValue, "derivedValue"),
      kind: text(row.kind, `${itemPath}.kind`),
      reason: text(row.reason, `${itemPath}.reason`),
      sourceValue: scalar(row.sourceValue, "sourceValue"),
    };
  });
}

function parseNormalization(value: unknown, path: string): LoomPresetNormalizationReceipt {
  const row = record(value, path);
  exactKeys(row, [
    "policyId", "sourceLengthUnit", "sourceBodyLengthNative",
    "targetBodyLengthM", "metresPerNativeUnit",
  ], path);
  if (row.policyId !== "loom.airshow-body-length-normalization/v1"
      || row.sourceLengthUnit !== "unbound" || row.targetBodyLengthM !== 10) {
    throw new Error(`${path} policy is unsupported`);
  }
  return {
    metresPerNativeUnit: positive(row.metresPerNativeUnit, `${path}.metresPerNativeUnit`),
    policyId: row.policyId,
    sourceBodyLengthNative: positive(row.sourceBodyLengthNative, `${path}.sourceBodyLengthNative`),
    sourceLengthUnit: row.sourceLengthUnit,
    targetBodyLengthM: row.targetBodyLengthM,
  };
}

function finiteJson(value: unknown, path: string, depth = 0): LoomPresetQcJson {
  if (depth > 12) throw new Error(`${path} is too deeply nested`);
  if (value === null || typeof value === "boolean" || typeof value === "string") return value;
  if (typeof value === "number") {
    if (!Number.isFinite(value)) throw new Error(`${path} contains a non-finite number`);
    return value;
  }
  if (Array.isArray(value)) return value.map((item, index) => finiteJson(item, `${path}[${index}]`, depth + 1));
  const item = record(value, path);
  return Object.fromEntries(Object.entries(item).map(([key, child]) => [
    key, finiteJson(child, `${path}.${key}`, depth + 1),
  ]));
}

function parseQc(
  value: unknown,
  path: string,
  evaluatorId: "loom.native-wing-body/v2" | "loom.native-wing-body/v3",
  definitionRevision: string,
): { acceptedAssetSha256: `sha256:${string}`; receipt: Readonly<Record<string, LoomPresetQcJson>> } {
  const qc = record(value, path);
  exactKeys(qc, [
    "status", "passed", "evaluatorId", "definitionRevision", "acceptedAssetSha256",
    "surfaceVertexCount", "surfaceFaceCount", "panelVertexCount", "panelFaceCount", "volumeM3",
    "seamMaxCoordinateGapM", "resolution", "surfaceQuality", "panelQuality",
  ], path);
  if (qc.status !== "passed" || qc.passed !== true || qc.evaluatorId !== evaluatorId
      || qc.definitionRevision !== definitionRevision
      || record(qc.surfaceQuality, `${path}.surfaceQuality`).passed !== true
      || record(qc.panelQuality, `${path}.panelQuality`).passed !== true) {
    throw new Error(`${path} is not definition-bound and passed`);
  }
  ["surfaceVertexCount", "surfaceFaceCount", "panelVertexCount", "panelFaceCount"]
    .forEach((key) => nonnegativeInteger(qc[key], `${path}.${key}`));
  positive(qc.volumeM3, `${path}.volumeM3`);
  if (typeof qc.seamMaxCoordinateGapM !== "number"
      || !Number.isFinite(qc.seamMaxCoordinateGapM) || qc.seamMaxCoordinateGapM < 0) {
    throw new Error(`${path}.seamMaxCoordinateGapM must be nonnegative`);
  }
  record(qc.resolution, `${path}.resolution`);
  const digest = sha256(qc.acceptedAssetSha256, `${path}.acceptedAssetSha256`)
    .replace(/^sha256:/, "");
  const receipt = finiteJson(qc, path);
  if (typeof receipt !== "object" || receipt === null || Array.isArray(receipt)) {
    throw new Error(`${path} is malformed`);
  }
  return {
    acceptedAssetSha256: `sha256:${digest}`,
    receipt: receipt as Readonly<Record<string, LoomPresetQcJson>>,
  };
}

function v2Replacement(value: unknown, path: string): {
  definition: LoomWingBodyDefinition;
  replacement: LoomAirshowNativePreset["replacement"];
} {
  const definition = record(value, path);
  exactKeys(definition, ["schema", "wing", "fuselage"], path);
  if (definition.schema !== "loom.wing-body-definition/v1") {
    throw new Error(`${path}.schema is unsupported`);
  }
  const wing = record(definition.wing, `${path}.wing`);
  const fuselage = record(definition.fuselage, `${path}.fuselage`);
  const placement = record(wing.placement, `${path}.wing.placement`);
  if (wing.sourceNodeId !== "wing" || wing.instanceNodeId !== "wing"
      || !Array.isArray(wing.transformNodeIds) || wing.transformNodeIds.length !== 0
      || wing.tip !== "flat" || wing.tipRadiusM !== 0 || placement.kind !== "translation"
      || !Array.isArray(placement.offsetM) || placement.offsetM.length !== 3
      || placement.offsetM.some((offset) => offset !== 0) || fuselage.sourceNodeId !== "fuselage") {
    throw new Error(`${path} does not map to the stable four-node authoring graph`);
  }
  const nodes = [
    { id: "wing", label: "Shape-normalized derived wing", type: "primitive", definition: { primitive: "wing", panels: [wing.panel], tip: wing.tip, tipRadiusM: wing.tipRadiusM } },
    { id: "fuselage", label: "Shape-normalized derived fuselage", type: "primitive", definition: { primitive: "fuselage", stations: fuselage.stations } },
    { id: "wing-partner", label: "Derived mirrored wing partner", type: "transform", inputNodeId: "wing", operation: { transform: "mirror", axis: "y", planeOffsetM: 0 } },
    { id: "vehicle", label: "Derived wing/body assembly", type: "boolean", operation: "union", inputNodeIds: ["fuselage", "wing", "wing-partner"] },
  ];
  const parsed = parseLoomAuthoringDocument({
    schema: "loom-authoring/v1", documentId: "airshow-preset",
    revision: 0, nodes, rootNodeIds: ["vehicle"],
  });
  return {
    definition: value as LoomWingBodyDefinition,
    replacement: { nodes: parsed.nodes, rootNodeIds: parsed.rootNodeIds },
  };
}

export function parseLoomAirshowNativePreset(
  value: unknown,
  path: string,
): LoomAirshowNativePreset {
  const row = record(value, path);
  const definition = record(row.definition, `${path}.definition`);
  const v3 = definition.schema === "loom.wing-body-definition/v2";
  const commonKeys = [
    "presetRevision", "sourceScope", "selectedComponents", "omittedComponents",
    "adaptations", "normalization", "definitionRevision", "definition", "qc",
  ];
  exactKeys(row, v3 ? [
    ...commonKeys, "evaluatorId", "topology", "unionAlgorithmId", "acceptedMeshSchema",
  ] : commonKeys, path);
  if (row.sourceScope !== "selected-components-only") {
    throw new Error(`${path}.sourceScope is unsupported`);
  }
  const definitionRevision = sha256(row.definitionRevision, `${path}.definitionRevision`);
  const common = {
    adaptations: parseAdaptations(row.adaptations, `${path}.adaptations`),
    definitionRevision,
    normalization: parseNormalization(row.normalization, `${path}.normalization`),
    omittedComponents: parseOmissions(row.omittedComponents, `${path}.omittedComponents`),
    presetRevision: sha256(row.presetRevision, `${path}.presetRevision`),
    selectedComponents: parseSelected(row.selectedComponents, `${path}.selectedComponents`),
    sourceScope: "selected-components-only" as const,
  };
  if (!v3) {
    const parsed = v2Replacement(row.definition, `${path}.definition`);
    const qc = parseQc(row.qc, `${path}.qc`, "loom.native-wing-body/v2", definitionRevision);
    return {
      ...common,
      acceptedAssetSha256: qc.acceptedAssetSha256,
      contractVersion: "v2",
      definition: parsed.definition,
      evaluatorId: "loom.native-wing-body/v2",
      qcReceipt: qc.receipt,
      replacement: parsed.replacement,
      topology: "canonical-starboard-wing-fuselage-union",
    };
  }
  if (row.evaluatorId !== "loom.native-wing-body/v3"
      || row.topology !== "selected-wing-fuselage-pairwise-union"
      || row.unionAlgorithmId !== "loom.implicit-guided-explicit-union/v1"
      || row.acceptedMeshSchema !== "loom.accepted-mesh/v3") {
    throw new Error(`${path} v3 contract discriminator is unsupported`);
  }
  const parsedDefinition = parseLoomWingBodyDefinitionV2(row.definition);
  const qc = parseQc(row.qc, `${path}.qc`, row.evaluatorId, definitionRevision);
  const replacement = createLoomPresetV3Replacement(parsedDefinition);
  const parsed = parseLoomAuthoringDocument({
    schema: "loom-authoring/v1", documentId: "airshow-preset-v3",
    revision: 0, ...replacement,
  });
  return {
    ...common,
    acceptedAssetSha256: qc.acceptedAssetSha256,
    acceptedMeshSchema: row.acceptedMeshSchema,
    contractVersion: "v3",
    definition: parsedDefinition,
    evaluatorId: row.evaluatorId,
    qcReceipt: qc.receipt,
    replacement: { nodes: parsed.nodes, rootNodeIds: parsed.rootNodeIds },
    topology: row.topology,
    unionAlgorithmId: row.unionAlgorithmId,
  };
}
